/**
 * Company-name helpers.
 *
 * Company used to be free text on several forms, so the same company can be
 * stored as "Meridian Tech SRL", "MERIDIAN TECH SRL", "Meridian Tech S.R.L."
 * or with a typo. Anything that groups or filters by company must resolve the
 * stored value against the registered company names first (same rules as
 * backend/services/companyResolver.js), then key on `companyKey`.
 */

const LEGAL_SUFFIX = /\s+(s\.?r\.?l\.?(-d)?|s\.?a\.?|ifn|sra|ra|snc|scs|pfa)\.?\s*$/i;

const collapseSpaces = (name) => String(name || '').trim().replace(/\s+/g, ' ');

/** Case- and whitespace-insensitive key for a company name. */
export const companyKey = (name) => collapseSpaces(name).toLowerCase();

/** companyKey without the legal suffix and punctuation. */
const looseKey = (name) => companyKey(name).replace(LEGAL_SUFFIX, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const cur = [i];
    for (let j = 1; j <= b.length; j += 1) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}

/**
 * Map a stored company name onto one of the registered names: exact
 * (case/whitespace-insensitive) → without legal suffix/punctuation → small
 * typo (edit distance ≤ 2, 8+ chars, single best candidate). Unmatched names
 * come back trimmed.
 */
export function resolveCompanyName(name, canonicalNames = []) {
  const cleaned = collapseSpaces(name);
  if (!cleaned) return '';
  const labels = (canonicalNames || []).map(collapseSpaces).filter(Boolean);

  const key = companyKey(cleaned);
  const exact = labels.find((l) => companyKey(l) === key);
  if (exact) return exact;

  const loose = looseKey(cleaned);
  if (loose) {
    const hits = labels.filter((l) => looseKey(l) === loose);
    if (hits.length === 1) return hits[0];
  }

  if (loose.length >= 8) {
    const scored = labels.map((l) => ({ l, d: levenshtein(loose, looseKey(l)) })).sort((a, b) => a.d - b.d);
    if (scored.length && scored[0].d <= 2 && (scored.length === 1 || scored[1].d > scored[0].d)) return scored[0].l;
  }
  return cleaned;
}

/**
 * Group cost rows by company. Each row's company is first resolved against
 * `canonicalNames` (Company labels from the API), so casing, suffix and typo
 * variants of the same company land in one bucket.
 *
 * @returns {Array<{ key, name, total, byType, costs }>} in first-seen order
 */
export function groupCostsByCompany(costs, canonicalNames = []) {
  const buckets = new Map();
  for (const cost of costs || []) {
    const name = resolveCompanyName(cost.company, canonicalNames);
    const key = companyKey(name);
    if (!key) continue;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { key, name, total: 0, byType: {}, costs: [] };
      buckets.set(key, bucket);
    }
    const amount = Number(cost.amount) || 0;
    const type = cost.costType || 'Other';
    bucket.total += amount;
    bucket.byType[type] = (bucket.byType[type] || 0) + amount;
    bucket.costs.push(cost);
  }
  return Array.from(buckets.values());
}

/**
 * Short chart labels that stay distinguishable: the legal suffix is dropped
 * and names are cut to two words, but when two companies would collide
 * ("Corporate Intelligence Agency SRL" / "Corporate Intelligence Advisors")
 * those get as many words as needed to tell them apart.
 *
 * @returns {Map<string, string>} full name → label
 */
export function shortCompanyLabels(names) {
  const words = (n) => String(n || '').replace(LEGAL_SUFFIX, '').trim().split(/\s+/).filter(Boolean);
  const result = new Map();
  let pending = [...new Set((names || []).filter(Boolean))];
  let count = 2;
  while (pending.length) {
    const depth = count;
    const groups = new Map();
    for (const n of pending) {
      const label = words(n).slice(0, depth).join(' ') || n;
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(n);
    }
    const next = [];
    for (const [label, members] of groups) {
      if (members.length === 1) result.set(members[0], label);
      else if (depth >= 6 || members.every((m) => words(m).length <= depth)) members.forEach((m) => result.set(m, m));
      else next.push(...members);
    }
    pending = next;
    count += 1;
  }
  return result;
}
